import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Pencil, X, Save, Shield, User, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TAB_PERMISSIONS = [
  { key: 'enquiries', label: 'Enquiries' },
  { key: 'blog', label: 'Blog' },
  { key: 'pricing', label: 'Pricing' },
  { key: 'products', label: 'Products' },
  { key: 'orders', label: 'Orders' },
  { key: 'discounts', label: 'Discounts' },
  { key: 'legal', label: 'Legal' },
  { key: 'contact', label: 'Contact' },
  { key: 'analytics', label: 'Analytics' },
];

function UserEditor({ user, onSave, onCancel }) {
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [password, setPassword] = useState('');
  const [permissions, setPermissions] = useState(user?.permissions || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const togglePerm = (key) => {
    setPermissions(prev => prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]);
  };

  const selectAll = () => setPermissions(TAB_PERMISSIONS.map(t => t.key));
  const selectNone = () => setPermissions([]);

  const handleSave = async () => {
    if (!email.trim()) { setError('Email is required'); return; }
    if (!user && !password.trim()) { setError('Password is required for new users'); return; }
    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('admin_token');
      const body = { email, name, permissions };
      if (password.trim()) body.password = password;
      const url = user?.id ? `${API}/api/admin/accounts/${user.id}` : `${API}/api/admin/accounts`;
      const method = user?.id ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      onSave(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="user-editor" className="bg-[#111111] border border-white/10 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-['Outfit'] text-lg font-bold text-white">{user?.id ? 'Edit User' : 'New User'}</h3>
        <button onClick={onCancel} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-white hover:bg-white/10">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-slate-400 block mb-1.5">Name</label>
            <input data-testid="user-name-input" value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#00E5FF]/50" placeholder="Staff name" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-400 block mb-1.5">Email</label>
            <input data-testid="user-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#00E5FF]/50" placeholder="user@example.com" />
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-slate-400 block mb-1.5">{user?.id ? 'New Password (leave blank to keep)' : 'Password'}</label>
          <input data-testid="user-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#00E5FF]/50" placeholder={user?.id ? 'Leave blank to keep current' : 'Set password'} />
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-xs font-medium text-slate-400">Page Permissions</label>
            <div className="flex gap-2">
              <button type="button" onClick={selectAll} className="text-[10px] font-medium text-[#00E5FF] hover:text-white transition-colors">Select all</button>
              <span className="text-slate-600 text-[10px]">|</span>
              <button type="button" onClick={selectNone} className="text-[10px] font-medium text-slate-500 hover:text-white transition-colors">Clear</button>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {TAB_PERMISSIONS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                data-testid={`perm-${key}`}
                onClick={() => togglePerm(key)}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium border transition-all ${
                  permissions.includes(key)
                    ? 'bg-[#00E5FF]/10 border-[#00E5FF]/30 text-[#00E5FF]'
                    : 'bg-white/[0.02] border-white/5 text-slate-500 hover:border-white/20'
                }`}
              >
                {permissions.includes(key) && <Check className="w-3.5 h-3.5" />}
                {label}
              </button>
            ))}
          </div>
        </div>

        {error && <p className="text-xs text-rose-400 bg-rose-500/10 rounded-lg px-3 py-2">{error}</p>}

        <div className="flex gap-3 pt-2">
          <button data-testid="user-save-btn" onClick={handleSave} disabled={saving} className="inline-flex items-center gap-2 bg-[#00E5FF] text-[#0A0A0A] px-6 py-3 rounded-xl text-sm font-semibold hover:bg-white transition-colors disabled:opacity-50">
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : (user?.id ? 'Update User' : 'Create User')}
          </button>
          <button onClick={onCancel} className="px-6 py-3 rounded-xl text-sm font-medium text-slate-400 border border-white/10 hover:text-white hover:border-white/20 transition-colors">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AccountManager() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API}/api/admin/accounts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setUsers(data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user? This cannot be undone.')) return;
    const token = localStorage.getItem('admin_token');
    const res = await fetch(`${API}/api/admin/accounts/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) setUsers(u => u.filter(x => x.id !== id));
  };

  const handleSave = (saved) => {
    if (editing?.id) {
      setUsers(u => u.map(x => x.id === saved.id ? saved : x));
    } else {
      setUsers(u => [...u, saved]);
    }
    setEditing(null);
  };

  if (editing !== null) {
    return <UserEditor user={editing.id ? editing : null} onSave={handleSave} onCancel={() => setEditing(null)} />;
  }

  return (
    <div data-testid="account-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-['Outfit'] text-xl font-bold text-white">Accounts</h2>
          <p className="text-sm text-slate-500 mt-0.5">Manage team access and permissions.</p>
        </div>
        <button data-testid="add-user-btn" onClick={() => setEditing({})} className="inline-flex items-center gap-2 bg-[#00E5FF] text-[#0A0A0A] px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-white transition-colors">
          <Plus className="w-4 h-4" />
          Add User
        </button>
      </div>

      {loading ? (
        <div className="text-center py-16">
          <div className="w-8 h-8 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-slate-500">Loading accounts...</p>
        </div>
      ) : (
        <div className="space-y-3">
          {users.map((user) => (
            <div key={user.id || user.email} data-testid={`user-row-${user.email}`} className="bg-[#111111] border border-white/5 rounded-xl p-5 hover:bg-white/[0.03] transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${user.role === 'admin' ? 'bg-[#00E5FF]/20' : 'bg-white/5'}`}>
                    {user.role === 'admin' ? <Shield className="w-5 h-5 text-[#00E5FF]" /> : <User className="w-5 h-5 text-slate-400" />}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-['Outfit'] text-sm font-semibold text-white">{user.name || user.email}</h3>
                      <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${user.role === 'admin' ? 'bg-[#00E5FF]/10 text-[#00E5FF]' : 'bg-white/5 text-slate-500'}`}>
                        {user.role}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{user.email}</p>
                    {user.role === 'staff' && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(user.permissions || []).length === 0 ? (
                          <span className="text-[10px] text-rose-400">No permissions</span>
                        ) : (
                          (user.permissions || []).map(p => (
                            <span key={p} className="text-[10px] font-medium text-slate-400 bg-white/5 px-2 py-0.5 rounded-md capitalize">{p}</span>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {user.role !== 'admin' && (
                  <div className="flex items-center gap-1">
                    <button data-testid={`edit-user-${user.email}`} onClick={() => setEditing(user)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-[#00E5FF] hover:bg-[#00E5FF]/10 transition-colors">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button data-testid={`delete-user-${user.email}`} onClick={() => handleDelete(user.id)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
